/**
 * Optional surgeon-entered patient ID.
 *
 * Stored encrypted at rest as `pid:v2:<lookupHex>.<base64url(iv||ct||tag)>`
 * (AES-256-GCM, account DEK from the server). A SHA-256 lookup token
 * (`pid:v1:<hex>`) is embedded so search/dedup stay equality checks without
 * decrypting every row.
 *
 * Legacy rows may still be `pid:v1:` hash-only (irreversible) — those stay
 * masked until the surgeon re-enters the ID. Plaintext must never persist.
 */

export const PATIENT_ID_HASH_PREFIX = 'pid:v1:';
export const PATIENT_ID_ENC_PREFIX = 'pid:v2:';

const HASH_HEX_RE = /^[0-9a-f]{64}$/;
const IV_LEN = 12;
const TAG_LEN = 16;

/** Normalize before hash / encrypt / compare — trim + uppercase. */
export function normalizePatientId(raw: string): string {
  return raw.trim().toUpperCase();
}

/** True when the stored value is a legacy one-way hash. */
export function isPatientIdHash(value: string | undefined | null): boolean {
  if (!value) return false;
  if (!value.startsWith(PATIENT_ID_HASH_PREFIX)) return false;
  return HASH_HEX_RE.test(value.slice(PATIENT_ID_HASH_PREFIX.length));
}

export type ParsedPatientIdEncrypted = {
  lookupHex: string;
  blob: Uint8Array;
};

/** True when the stored value is reversible AES-GCM ciphertext (`pid:v2:`). */
export function isPatientIdEncrypted(value: string | undefined | null): boolean {
  return parsePatientIdEncrypted(value) !== null;
}

/** Hash-only or encrypted sealed forms (never show/store as "open" plaintext). */
export function isPatientIdSealed(value: string | undefined | null): boolean {
  return isPatientIdHash(value) || isPatientIdEncrypted(value);
}

export function parsePatientIdEncrypted(
  value: string | undefined | null,
): ParsedPatientIdEncrypted | null {
  if (!value || !value.startsWith(PATIENT_ID_ENC_PREFIX)) return null;
  const rest = value.slice(PATIENT_ID_ENC_PREFIX.length);
  const dot = rest.indexOf('.');
  if (dot !== 64) return null;
  const lookupHex = rest.slice(0, 64);
  const b64 = rest.slice(65);
  if (!HASH_HEX_RE.test(lookupHex) || !b64) return null;
  try {
    const blob = base64UrlToBytes(b64);
    if (blob.length < IV_LEN + TAG_LEN + 1) return null;
    return { lookupHex, blob };
  } catch {
    return null;
  }
}

/** Equality token for search/dedup — `pid:v1:<hex>` from v1 or v2 rows. */
export function patientIdLookupToken(stored: string | undefined | null): string | null {
  if (!stored) return null;
  if (isPatientIdHash(stored)) return stored;
  const enc = parsePatientIdEncrypted(stored);
  if (enc) return `${PATIENT_ID_HASH_PREFIX}${enc.lookupHex}`;
  return null;
}

/** Last-4 hint for masked display (from the raw ID before sealing). */
export function patientIdHintFromRaw(raw: string): string {
  const n = normalizePatientId(raw);
  if (!n) return '';
  return n.length <= 4 ? n : n.slice(-4);
}

/** Canonical hash input — must match api/lib/patient-id-seal.ts. */
export function patientIdHashMaterial(raw: string, accountKey: string): string {
  return `suturia:pid:v1:${accountKey}:${normalizePatientId(raw)}`;
}

/**
 * One-way lookup seal of a patient ID (Web Crypto — browser + Node 22).
 * Returns `pid:v1:<hex>`.
 */
export async function hashPatientId(raw: string, accountKey: string): Promise<string> {
  const material = patientIdHashMaterial(raw, accountKey);
  const digest = await globalThis.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(material),
  );
  const hex = [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `${PATIENT_ID_HASH_PREFIX}${hex}`;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  const b64 =
    typeof btoa === 'function'
      ? btoa(bin)
      : Buffer.from(bytes).toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlToBytes(b64url: string): Uint8Array {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  const raw = b64 + pad;
  if (typeof atob === 'function') {
    const bin = atob(raw);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  return new Uint8Array(Buffer.from(raw, 'base64'));
}

function asBufferSource(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function importAesKey(dek: Uint8Array) {
  return globalThis.crypto.subtle.importKey(
    'raw',
    asBufferSource(dek),
    'AES-GCM',
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Encrypt a patient ID → `pid:v2:<lookupHex>.<blob>`.
 * `dek` is the 32-byte account key from auth.bootstrap.
 */
export async function encryptPatientId(
  raw: string,
  accountKey: string,
  dek: Uint8Array,
): Promise<string> {
  const normalized = normalizePatientId(raw);
  const lookup = await hashPatientId(normalized, accountKey);
  const lookupHex = lookup.slice(PATIENT_ID_HASH_PREFIX.length);
  const key = await importAesKey(dek);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_LEN));
  const ct = new Uint8Array(
    await globalThis.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(normalized),
    ),
  ); // tag is appended by Web Crypto
  // Web Crypto appends the 16-byte tag to the ciphertext.
  const blob = new Uint8Array(iv.length + ct.length);
  blob.set(iv, 0);
  blob.set(ct, iv.length);
  return `${PATIENT_ID_ENC_PREFIX}${lookupHex}.${bytesToBase64Url(blob)}`;
}

/** Decrypt `pid:v2:` → normalized plaintext, or null if not decryptable. */
export async function decryptPatientId(
  sealed: string,
  dek: Uint8Array,
): Promise<string | null> {
  const parsed = parsePatientIdEncrypted(sealed);
  if (!parsed) return null;
  const { blob } = parsed;
  const iv = blob.slice(0, IV_LEN);
  const data = blob.slice(IV_LEN);
  try {
    const key = await importAesKey(dek);
    const pt = await globalThis.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data,
    );
    return new TextDecoder().decode(pt);
  } catch {
    return null;
  }
}

/**
 * Display label: full ID when `plaintext` is known; otherwise masked last-4
 * for legacy `pid:v1:` / when DEK is unavailable.
 */
export function formatStoredPatientId(
  paciente: {
    historiaClinica?: string;
    historiaClinicaHint?: string;
  },
  opts?: { plaintext?: string | null },
): string | null {
  const plain = opts?.plaintext?.trim();
  if (plain) return plain;

  const hint = paciente.historiaClinicaHint?.trim();
  if (hint) return `····${hint}`;
  if (isPatientIdSealed(paciente.historiaClinica)) return '····';
  // Legacy plaintext still on disk: mask rather than echo the full value.
  const legacy = paciente.historiaClinica?.trim();
  if (!legacy) return null;
  const tail = legacy.length <= 4 ? legacy : legacy.slice(-4);
  return `····${tail.toUpperCase()}`;
}

/**
 * Build sealed paciente fields from a raw form value, or preserve an
 * existing sealed ID when the form field was left blank on edit.
 *
 * When `dek` is present, new IDs are stored as reversible `pid:v2:`.
 * Without DEK (tests / offline edge), falls back to irreversible `pid:v1:`.
 */
export async function sealPatientIdFields(opts: {
  rawInput: string;
  accountKey: string;
  dek?: Uint8Array | null;
  previous?: { historiaClinica?: string; historiaClinicaHint?: string };
}): Promise<{ historiaClinica?: string; historiaClinicaHint?: string }> {
  const trimmed = opts.rawInput.trim();
  if (trimmed) {
    // Do not re-seal an already-sealed value pasted into the field.
    if (isPatientIdSealed(trimmed)) {
      return {
        historiaClinica: trimmed,
        ...(opts.previous?.historiaClinicaHint
          ? { historiaClinicaHint: opts.previous.historiaClinicaHint }
          : {}),
      };
    }
    const historiaClinica = opts.dek
      ? await encryptPatientId(trimmed, opts.accountKey, opts.dek)
      : await hashPatientId(trimmed, opts.accountKey);
    return {
      historiaClinica,
      historiaClinicaHint: patientIdHintFromRaw(trimmed),
    };
  }

  const prev = opts.previous?.historiaClinica?.trim();
  if (!prev) return {};

  if (isPatientIdSealed(prev)) {
    return {
      historiaClinica: prev,
      ...(opts.previous?.historiaClinicaHint
        ? { historiaClinicaHint: opts.previous.historiaClinicaHint }
        : {}),
    };
  }

  // Legacy plaintext cleared on edit → drop it.
  return {};
}
